import { useEffect, useMemo, useState, type ReactNode } from 'react';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PersonAddAlt1RoundedIcon from '@mui/icons-material/PersonAddAlt1Rounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';
import TrendingFlatRoundedIcon from '@mui/icons-material/TrendingFlatRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import {
    Alert,
    Avatar,
    Box,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Title } from 'react-admin';

import {
    getBusinessOverview,
    type BusinessOverviewCountrySnapshot,
    type BusinessOverviewData,
    type BusinessOverviewHourlyTrendPoint,
    type BusinessOverviewOrderDetail,
    type BusinessOverviewSalesByCountry,
    type BusinessOverviewSalesByCurrency,
    type BusinessOverviewWindowMetric,
} from '../admin/adminBusinessOverviewApi';
import { formatApiDateTime } from '../admin/dateTimeUtils';

const defaultBaseCurrency = 'AUD';
const businessOverviewRefreshIntervalMs = 10000;

function formatNumber(value: number) {
    return new Intl.NumberFormat().format(value);
}

function formatDecimal(value: number) {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(value);
}

function formatMetricValue(value: number) {
    return Number.isInteger(value) ? formatNumber(value) : formatDecimal(value);
}

function formatCurrency(value: number, currency: string) {
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: currency || defaultBaseCurrency,
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${formatNumber(value)} ${currency || defaultBaseCurrency}`;
    }
}

function formatDate(value: string, options?: Intl.DateTimeFormatOptions) {
    return formatApiDateTime(value, options ?? {});
}

function formatSydneyTimeLabel(value: string) {
    const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!match) return value;

    const hour = Number(match[1]);
    if (hour < 0 || hour > 23) return value;

    const period = hour >= 12 ? 'PM' : 'AM';
    const twelveHour = hour % 12 || 12;
    return `${twelveHour}:${match[2]} ${period}`;
}

function formatCountryCode(value: string) {
    if (!value) return 'N/A';
    if (value === 'UNRESOLVED') return 'Unresolved';
    return value;
}

function formatPercent(value: number) {
    return new Intl.NumberFormat(undefined, {
        style: 'percent',
        maximumFractionDigits: Math.abs(value) >= 0.1 ? 0 : 1,
    }).format(value);
}

function formatOrderStatus(value: string) {
    return value ? value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Unknown';
}

function getOrderStatusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
    if (status === 'succeeded') return 'success';
    if (status === 'failed' || status === 'canceled') return 'error';
    if (status === 'awaiting_payment') return 'warning';
    if (status === 'pending') return 'info';
    return 'default';
}

function buildFilteredSalesByCurrency(rows: BusinessOverviewSalesByCountry[]): BusinessOverviewSalesByCurrency[] {
    const grouped = new Map<string, BusinessOverviewSalesByCurrency>();

    for (const row of rows) {
        const current = grouped.get(row.currency) ?? {
            currency: row.currency,
            purchasedUsers: 0,
            purchaseCount: 0,
            totalAmount: 0,
            approximateAmountInBaseCurrency: 0,
        };

        current.purchasedUsers += row.purchasedUsers;
        current.purchaseCount += row.purchaseCount;
        current.totalAmount += row.totalAmount;
        current.approximateAmountInBaseCurrency += row.approximateAmountInBaseCurrency;
        grouped.set(row.currency, current);
    }

    return Array.from(grouped.values()).sort((a, b) => {
        if (b.approximateAmountInBaseCurrency !== a.approximateAmountInBaseCurrency) {
            return b.approximateAmountInBaseCurrency - a.approximateAmountInBaseCurrency;
        }

        return a.currency.localeCompare(b.currency);
    });
}

function getTrendMeta(today: number, yesterday: number) {
    const delta = today - yesterday;
    const ratio = yesterday > 0 ? delta / yesterday : null;

    return {
        delta,
        ratio,
        direction: delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat',
    } as const;
}

function getTrendChipLabel(metric: BusinessOverviewWindowMetric) {
    const trend = getTrendMeta(metric.today, metric.yesterday);

    if (trend.delta === 0) {
        return 'No change';
    }

    return `${trend.delta > 0 ? '+' : ''}${formatNumber(trend.delta)}${trend.ratio !== null ? ` • ${formatPercent(Math.abs(trend.ratio))}` : ''}`;
}

function getTrendColor(direction: ReturnType<typeof getTrendMeta>['direction']) {
    if (direction === 'up') return 'success';
    if (direction === 'down') return 'error';
    return 'default';
}

function getTrendIcon(direction: ReturnType<typeof getTrendMeta>['direction']) {
    if (direction === 'up') return <TrendingUpRoundedIcon />;
    if (direction === 'down') return <TrendingDownRoundedIcon />;
    return <TrendingFlatRoundedIcon />;
}

function SectionCard({
    title,
    subtitle,
    action,
    children,
}: {
    title: string;
    subtitle?: string;
    action?: ReactNode;
    children: ReactNode;
}) {
    return (
        <Card
            variant="outlined"
            sx={{
                width: '100%',
                minWidth: 0,
                borderRadius: 4,
                borderColor: 'rgba(15, 23, 42, 0.08)',
                backgroundColor: 'rgba(255, 255, 255, 0.92)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 18px 42px rgba(15, 23, 42, 0.06)',
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Stack spacing={2.5}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1.5}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', md: 'center' }}
                    >
                        <Stack spacing={0.5}>
                            <Typography variant="h6">{title}</Typography>
                            {subtitle ? (
                                <Typography variant="body2" color="text.secondary">
                                    {subtitle}
                                </Typography>
                            ) : null}
                        </Stack>
                        {action}
                    </Stack>
                    {children}
                </Stack>
            </CardContent>
        </Card>
    );
}

function SummaryTile({
    label,
    value,
    accent,
    helperText,
}: {
    label: string;
    value: string;
    accent?: string;
    helperText?: string;
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                minWidth: 0,
                p: 1.35,
                borderRadius: 3,
                borderColor: accent ? alpha(accent, 0.16) : 'rgba(148, 163, 184, 0.18)',
                bgcolor: accent ? alpha(accent, 0.08) : 'rgba(248, 250, 252, 0.84)',
            }}
        >
            <Stack spacing={0.45}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
                >
                    {label}
                </Typography>
                <Typography
                    variant="subtitle1"
                    fontWeight={700}
                    sx={{ lineHeight: 1.2, overflowWrap: 'anywhere' }}
                >
                    {value}
                </Typography>
                {helperText ? (
                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        {helperText}
                    </Typography>
                ) : null}
            </Stack>
        </Paper>
    );
}

function getMobileRailSx() {
    return {
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: undefined },
        overflowX: 'visible',
        px: 0,
        pb: 0,
        mx: 0,
        scrollSnapType: 'none',
        '& > *': {
            minWidth: 0,
        },
    };
}

function KpiCard({
    label,
    value,
    caption,
    icon,
    accent,
    metric,
}: {
    label: string;
    value: string;
    caption: string;
    icon: ReactNode;
    accent: string;
    metric?: BusinessOverviewWindowMetric;
}) {
    const trend = metric ? getTrendMeta(metric.today, metric.yesterday) : null;

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderRadius: 4,
                borderColor: alpha(accent, 0.2),
                background: `linear-gradient(180deg, ${alpha(accent, 0.12)} 0%, rgba(255, 255, 255, 0.94) 68%)`,
                boxShadow: '0 20px 44px rgba(15, 23, 42, 0.08)',
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                        alignItems="flex-start"
                    >
                        <Avatar
                            variant="rounded"
                            sx={{
                                bgcolor: alpha(accent, 0.14),
                                color: accent,
                                width: 46,
                                height: 46,
                                borderRadius: 3,
                            }}
                        >
                            {icon}
                        </Avatar>
                        {trend ? (
                            <Chip
                                size="small"
                                color={getTrendColor(trend.direction)}
                                icon={getTrendIcon(trend.direction)}
                                label={metric ? getTrendChipLabel(metric) : 'No change'}
                            />
                        ) : null}
                    </Stack>
                    <Stack spacing={0.75}>
                        <Typography variant="overline" color="text.secondary">
                            {label}
                        </Typography>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                            {value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {caption}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function MetricTrendCard({
    label,
    metric,
    caption,
    icon,
    accent,
}: {
    label: string;
    metric: BusinessOverviewWindowMetric;
    caption: string;
    icon: ReactNode;
    accent: string;
}) {
    const trend = getTrendMeta(metric.today, metric.yesterday);
    const weeklyAverage = metric.currentWeekMondayToSunday / 7;
    const bars = [
        { label: 'Yesterday', value: metric.yesterday, color: 'rgba(100, 116, 139, 0.55)', display: formatNumber(metric.yesterday) },
        { label: 'Today', value: metric.today, color: accent, display: formatNumber(metric.today) },
        {
            label: 'Daily Avg',
            value: weeklyAverage,
            color: alpha(accent, 0.65),
            display: formatMetricValue(weeklyAverage),
        },
    ];
    const maxValue = Math.max(...bars.map((bar) => bar.value), 1);

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                borderRadius: 4,
                borderColor: 'rgba(15, 23, 42, 0.08)',
                boxShadow: '0 18px 42px rgba(15, 23, 42, 0.05)',
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 2.5 }, height: '100%' }}>
                <Stack spacing={2} sx={{ height: '100%' }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                        alignItems="flex-start"
                    >
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Avatar
                                variant="rounded"
                                sx={{
                                    bgcolor: alpha(accent, 0.12),
                                    color: accent,
                                    width: 40,
                                    height: 40,
                                    borderRadius: 3,
                                }}
                            >
                                {icon}
                            </Avatar>
                            <Typography variant="subtitle1">{label}</Typography>
                        </Stack>
                        <Chip
                            size="small"
                            color={getTrendColor(trend.direction)}
                            icon={getTrendIcon(trend.direction)}
                            label={getTrendChipLabel(metric)}
                        />
                    </Stack>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                        alignItems={{ xs: 'flex-start', sm: 'flex-end' }}
                    >
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
                            {formatNumber(metric.today)}
                        </Typography>
                        <Stack spacing={0.25} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                            <Typography variant="caption" color="text.secondary">
                                Week total
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {formatNumber(metric.currentWeekMondayToSunday)}
                            </Typography>
                        </Stack>
                    </Stack>

                    <Typography variant="body2" color="text.secondary">
                        {caption}
                    </Typography>

                    <Stack spacing={1.2}>
                        {bars.map((bar) => (
                            <Stack key={bar.label} direction="row" spacing={1} alignItems="center">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ width: { xs: 62, sm: 70 }, flexShrink: 0 }}
                                >
                                    {bar.label}
                                </Typography>
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: 9,
                                        bgcolor: 'rgba(148, 163, 184, 0.16)',
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: `${Math.max((bar.value / maxValue) * 100, bar.value > 0 ? 8 : 0)}%`,
                                            height: '100%',
                                            bgcolor: bar.color,
                                            borderRadius: 999,
                                        }}
                                    />
                                </Box>
                                <Typography variant="caption" sx={{ minWidth: { xs: 48, sm: 58 }, textAlign: 'right' }}>
                                    {bar.display}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function WindowMetricTable({
    title,
    subtitle,
    metrics,
    mobile,
}: {
    title: string;
    subtitle?: string;
    metrics: Array<{ label: string; value: BusinessOverviewWindowMetric }>;
    mobile?: boolean;
}) {
    if (mobile) {
        return (
            <SectionCard title={title} subtitle={subtitle}>
                <Stack spacing={1.5}>
                    {metrics.map((metric) => (
                        <Paper
                            key={metric.label}
                            variant="outlined"
                            sx={{
                                minWidth: 0,
                                p: 1.5,
                                borderRadius: 3,
                                borderColor: 'rgba(15, 23, 42, 0.08)',
                                bgcolor: 'rgba(248, 250, 252, 0.72)',
                            }}
                        >
                            <Stack spacing={1.25}>
                                <Typography variant="subtitle2">{metric.label}</Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gap: 1,
                                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' },
                                    }}
                                >
                                    <SummaryTile label="Today" value={formatNumber(metric.value.today)} accent="#2563eb" />
                                    <SummaryTile
                                        label="Yesterday"
                                        value={formatNumber(metric.value.yesterday)}
                                        accent="#475569"
                                    />
                                    <SummaryTile
                                        label="This Week"
                                        value={formatNumber(metric.value.currentWeekMondayToSunday)}
                                        accent="#0f766e"
                                    />
                                </Box>
                            </Stack>
                        </Paper>
                    ))}
                </Stack>
            </SectionCard>
        );
    }

    return (
        <SectionCard title={title} subtitle={subtitle}>
            <TableContainer sx={{ maxHeight: 380 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Metric</TableCell>
                            <TableCell align="right">Today</TableCell>
                            <TableCell align="right">Yesterday</TableCell>
                            <TableCell align="right">This Week</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {metrics.map((metric) => (
                            <TableRow key={metric.label} hover>
                                <TableCell>{metric.label}</TableCell>
                                <TableCell align="right">{formatNumber(metric.value.today)}</TableCell>
                                <TableCell align="right">{formatNumber(metric.value.yesterday)}</TableCell>
                                <TableCell align="right">{formatNumber(metric.value.currentWeekMondayToSunday)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
}

function HourlyComparisonChart({
    title,
    points,
    accent,
    todayDate,
    yesterdayDate,
}: {
    title: string;
    points: BusinessOverviewHourlyTrendPoint[];
    accent: string;
    todayDate: string;
    yesterdayDate: string;
}) {
    if (points.length === 0) {
        return (
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4, minWidth: 0 }}>
                <Stack spacing={1}>
                    <Typography variant="subtitle1">{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Hourly comparison data is not available yet.
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    const width = 760;
    const height = 300;
    const padding = { top: 24, right: 20, bottom: 46, left: 52 };
    const plotWidth = width - padding.left - padding.right;
    const plotHeight = height - padding.top - padding.bottom;
    const maxValue = Math.max(...points.flatMap((point) => [point.today, point.yesterday]), 1);
    const x = (index: number) =>
        padding.left + (points.length === 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth);
    const y = (value: number) => padding.top + plotHeight - (value / maxValue) * plotHeight;
    const buildPath = (key: 'today' | 'yesterday') =>
        points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${x(index)} ${y(point[key])}`).join(' ');
    const gridLines = Array.from({ length: 5 }, (_, index) => {
        const value = (maxValue * index) / 4;
        return { value, y: y(value) };
    });
    const labelIndexes = Array.from(
        new Set([0, Math.floor((points.length - 1) / 2), points.length - 1]),
    );
    const latest = points.at(-1) ?? points[0];
    const delta = latest.today - latest.yesterday;
    const latestTimeLabel = formatSydneyTimeLabel(latest.timeLabel);

    return (
        <Paper
            variant="outlined"
            sx={{
                p: { xs: 2, md: 2.5 },
                borderRadius: 4,
                borderColor: alpha(accent, 0.18),
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <Stack spacing={2}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    spacing={1}
                >
                    <Stack spacing={0.35}>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Cumulative count through the same time on both days
                        </Typography>
                    </Stack>
                    <Chip
                        size="small"
                        color={getTrendColor(delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat')}
                        icon={getTrendIcon(delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat')}
                        label={`${delta > 0 ? '+' : ''}${formatNumber(delta)} at ${latestTimeLabel}`}
                    />
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 1,
                    }}
                >
                    <SummaryTile label="Today" value={formatNumber(latest.today)} accent={accent} helperText={todayDate} />
                    <SummaryTile
                        label="Yesterday"
                        value={formatNumber(latest.yesterday)}
                        helperText={yesterdayDate}
                    />
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <SummaryTile
                            label="Compared at"
                            value={latestTimeLabel}
                            helperText="Sydney local time"
                        />
                    </Box>
                </Box>

                <Box sx={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                    <Box
                        component="svg"
                        viewBox={`0 0 ${width} ${height}`}
                        role="img"
                        aria-label={`${title}: today versus yesterday cumulative hourly trend`}
                        sx={{ display: 'block', width: '100%', minWidth: { xs: 520, sm: 0 }, height: 'auto' }}
                    >
                        {gridLines.map((line) => (
                            <g key={line.value}>
                                <line
                                    x1={padding.left}
                                    x2={width - padding.right}
                                    y1={line.y}
                                    y2={line.y}
                                    stroke="rgba(148, 163, 184, 0.22)"
                                    strokeWidth="1"
                                />
                                <text
                                    x={padding.left - 10}
                                    y={line.y + 4}
                                    textAnchor="end"
                                    fontSize="11"
                                    fill="#64748b"
                                >
                                    {formatMetricValue(line.value)}
                                </text>
                            </g>
                        ))}
                        <path
                            d={buildPath('yesterday')}
                            fill="none"
                            stroke="#94a3b8"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d={buildPath('today')}
                            fill="none"
                            stroke={accent}
                            strokeWidth="4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        {points.map((point, index) => (
                            <g key={`${point.timeLabel}-${index}`}>
                                <circle cx={x(index)} cy={y(point.yesterday)} r="3.5" fill="#94a3b8">
                                    <title>{`${yesterdayDate} ${formatSydneyTimeLabel(point.timeLabel)}: ${formatNumber(point.yesterday)}`}</title>
                                </circle>
                                <circle cx={x(index)} cy={y(point.today)} r="4" fill={accent}>
                                    <title>{`${todayDate} ${formatSydneyTimeLabel(point.timeLabel)}: ${formatNumber(point.today)}`}</title>
                                </circle>
                            </g>
                        ))}
                        {labelIndexes.map((index) => (
                            <text
                                key={index}
                                x={x(index)}
                                y={height - 17}
                                textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'}
                                fontSize="12"
                                fill="#64748b"
                            >
                                {formatSydneyTimeLabel(points[index].timeLabel)}
                            </text>
                        ))}
                    </Box>
                </Box>

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 18, height: 3, borderRadius: 99, bgcolor: accent }} />
                        <Typography variant="caption">Today</Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <Box sx={{ width: 18, height: 3, borderRadius: 99, bgcolor: '#94a3b8' }} />
                        <Typography variant="caption">Yesterday</Typography>
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
    );
}

function OrdersTodayTable({
    rows,
    orderCount,
    returnedDetailCount,
    isTruncated,
    mobile,
}: {
    rows: BusinessOverviewOrderDetail[];
    orderCount: number;
    returnedDetailCount: number;
    isTruncated: boolean;
    mobile?: boolean;
}) {
    const subtitle = isTruncated
        ? `Showing the ${formatNumber(returnedDetailCount)} most recent of ${formatNumber(orderCount)} orders created today.`
        : `${formatNumber(orderCount)} orders created today, including pending, successful, failed, and canceled payment attempts.`;

    if (mobile) {
        return (
            <SectionCard title="Orders Created Today" subtitle={subtitle}>
                <Stack spacing={1.5}>
                    {rows.map((row) => (
                        <Card key={row.paymentIntentId} variant="outlined" sx={{ borderRadius: 3.5 }}>
                            <CardContent sx={{ p: 1.75 }}>
                                <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                                        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1" sx={{ overflowWrap: 'anywhere' }}>
                                                {row.userName || row.userEmail || 'Unknown user'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                                {row.userEmail || row.userId}
                                            </Typography>
                                        </Stack>
                                        <Chip size="small" color={getOrderStatusColor(row.status)} label={formatOrderStatus(row.status)} />
                                    </Stack>
                                    <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
                                        <SummaryTile label="Plan" value={row.planName || 'N/A'} />
                                        <SummaryTile label="Amount" value={formatCurrency(row.finalAmount, row.billingCurrency)} />
                                        <SummaryTile label="Country" value={formatCountryCode(row.billingCountryCode)} />
                                        <SummaryTile label="Created" value={formatDate(row.createdAtUtc, { dateStyle: 'short', timeStyle: 'short' })} />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                                        Order {row.paymentIntentId}{row.couponCode ? ` • Coupon ${row.couponCode}` : ''}
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {rows.length === 0 ? <Alert severity="info">No orders have been created today.</Alert> : null}
                </Stack>
            </SectionCard>
        );
    }

    return (
        <SectionCard title="Orders Created Today" subtitle={subtitle}>
            <TableContainer sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Created</TableCell>
                            <TableCell>User</TableCell>
                            <TableCell>Plan</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Country</TableCell>
                            <TableCell align="right">Amount</TableCell>
                            <TableCell>Order ID</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.paymentIntentId} hover>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                    {formatDate(row.createdAtUtc, { dateStyle: 'short', timeStyle: 'short' })}
                                </TableCell>
                                <TableCell>
                                    <Stack spacing={0.2}>
                                        <Typography variant="body2">{row.userName || 'N/A'}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {row.userEmail || row.userId}
                                        </Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>{row.planName || formatOrderStatus(row.purchaseType)}</TableCell>
                                <TableCell>
                                    <Chip size="small" color={getOrderStatusColor(row.status)} label={formatOrderStatus(row.status)} />
                                </TableCell>
                                <TableCell>{formatCountryCode(row.billingCountryCode)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.finalAmount, row.billingCurrency)}</TableCell>
                                <TableCell sx={{ maxWidth: 190, overflowWrap: 'anywhere' }}>{row.paymentIntentId}</TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center">No orders have been created today.</TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
}

function SalesByCountryTable({
    rows,
    baseCurrency,
    mobile,
}: {
    rows: BusinessOverviewSalesByCountry[];
    baseCurrency: string;
    mobile?: boolean;
}) {
    if (mobile) {
        return (
            <SectionCard
                title="Sales by Country"
                subtitle="Successful purchases grouped by billing market and transaction currency for today."
            >
                <Stack spacing={1.5}>
                    {rows.map((row) => (
                        <Card
                            key={`${row.countryCode}-${row.currency}`}
                            variant="outlined"
                            sx={{
                                minWidth: 0,
                                borderRadius: 3.5,
                                borderColor: 'rgba(15, 23, 42, 0.08)',
                                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                            }}
                        >
                            <CardContent sx={{ p: 1.75 }}>
                                <Stack spacing={1.5}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        spacing={1.5}
                                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                    >
                                        <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1">{formatCountryCode(row.countryCode)}</Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ overflowWrap: 'anywhere' }}
                                            >
                                                Currency {row.currency || 'N/A'}
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            size="small"
                                            label={formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                            sx={{
                                                maxWidth: '100%',
                                                alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                                bgcolor: alpha('#0f766e', 0.12),
                                                color: '#0f766e',
                                                '& .MuiChip-label': {
                                                    display: 'block',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                },
                                            }}
                                        />
                                    </Stack>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1,
                                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                                        }}
                                    >
                                        <SummaryTile label="Purchased Users" value={formatNumber(row.purchasedUsers)} accent="#2563eb" />
                                        <SummaryTile label="Purchases" value={formatNumber(row.purchaseCount)} accent="#7c3aed" />
                                        <SummaryTile
                                            label="Local Total"
                                            value={formatCurrency(row.totalAmount, row.currency)}
                                            accent="#ea580c"
                                        />
                                        <SummaryTile
                                            label={`Approx. ${baseCurrency}`}
                                            value={formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                            accent="#0f766e"
                                        />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {rows.length === 0 ? <Alert severity="info">No sales recorded for today.</Alert> : null}
                </Stack>
            </SectionCard>
        );
    }

    return (
        <SectionCard
            title="Sales by Country"
            subtitle="Successful purchases grouped by billing market and transaction currency for today."
        >
            <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell>Currency</TableCell>
                            <TableCell align="right">Purchased Users</TableCell>
                            <TableCell align="right">Purchases</TableCell>
                            <TableCell align="right">Local Total</TableCell>
                            <TableCell align="right">Approx. {baseCurrency}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={`${row.countryCode}-${row.currency}`} hover>
                                <TableCell>{formatCountryCode(row.countryCode)}</TableCell>
                                <TableCell>{row.currency || 'N/A'}</TableCell>
                                <TableCell align="right">{formatNumber(row.purchasedUsers)}</TableCell>
                                <TableCell align="right">{formatNumber(row.purchaseCount)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.totalAmount, row.currency)}</TableCell>
                                <TableCell align="right">
                                    {formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No sales recorded for today.
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
}

function SalesByCurrencyTable({
    rows,
    baseCurrency,
    mobile,
}: {
    rows: BusinessOverviewSalesByCurrency[];
    baseCurrency: string;
    mobile?: boolean;
}) {
    if (mobile) {
        return (
            <SectionCard
                title="Sales by Currency"
                subtitle="Currency-level totals for today after applying the current market scope."
            >
                <Stack spacing={1.5}>
                    {rows.map((row) => (
                        <Card
                            key={row.currency}
                            variant="outlined"
                            sx={{
                                minWidth: 0,
                                borderRadius: 3.5,
                                borderColor: 'rgba(15, 23, 42, 0.08)',
                                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                            }}
                        >
                            <CardContent sx={{ p: 1.75 }}>
                                <Stack spacing={1.5}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        spacing={1.5}
                                        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                    >
                                        <Stack spacing={0.35} sx={{ minWidth: 0 }}>
                                            <Typography variant="subtitle1">{row.currency || 'N/A'}</Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ overflowWrap: 'anywhere' }}
                                            >
                                                Daily billing total in the selected scope
                                            </Typography>
                                        </Stack>
                                        <Chip
                                            size="small"
                                            label={formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                            sx={{
                                                maxWidth: '100%',
                                                alignSelf: { xs: 'flex-start', sm: 'flex-start' },
                                                bgcolor: alpha('#15803d', 0.12),
                                                color: '#15803d',
                                                '& .MuiChip-label': {
                                                    display: 'block',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'anywhere',
                                                },
                                            }}
                                        />
                                    </Stack>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1,
                                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                                        }}
                                    >
                                        <SummaryTile label="Purchased Users" value={formatNumber(row.purchasedUsers)} accent="#2563eb" />
                                        <SummaryTile label="Purchases" value={formatNumber(row.purchaseCount)} accent="#7c3aed" />
                                        <SummaryTile
                                            label="Local Total"
                                            value={formatCurrency(row.totalAmount, row.currency)}
                                            accent="#ea580c"
                                        />
                                        <SummaryTile
                                            label={`Approx. ${baseCurrency}`}
                                            value={formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                            accent="#15803d"
                                        />
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {rows.length === 0 ? <Alert severity="info">No currency totals available for today.</Alert> : null}
                </Stack>
            </SectionCard>
        );
    }

    return (
        <SectionCard
            title="Sales by Currency"
            subtitle="Currency-level totals for today after applying the current market scope."
        >
            <TableContainer sx={{ maxHeight: 460 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Currency</TableCell>
                            <TableCell align="right">Purchased Users</TableCell>
                            <TableCell align="right">Purchases</TableCell>
                            <TableCell align="right">Local Total</TableCell>
                            <TableCell align="right">Approx. {baseCurrency}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.currency} hover>
                                <TableCell>{row.currency || 'N/A'}</TableCell>
                                <TableCell align="right">{formatNumber(row.purchasedUsers)}</TableCell>
                                <TableCell align="right">{formatNumber(row.purchaseCount)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.totalAmount, row.currency)}</TableCell>
                                <TableCell align="right">
                                    {formatCurrency(row.approximateAmountInBaseCurrency, baseCurrency)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    No currency totals available for today.
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
}

function CountrySnapshotsTable({
    rows,
    mobile,
}: {
    rows: BusinessOverviewCountrySnapshot[];
    mobile?: boolean;
}) {
    if (mobile) {
        return (
            <SectionCard
                title="Country Snapshots"
                subtitle="Country-level today and weekly activity, including the new chat activity windows."
            >
                <Stack spacing={1.5}>
                    {rows.map((snapshot) => (
                        <Card
                            key={snapshot.countryCode}
                            variant="outlined"
                            sx={{
                                minWidth: 0,
                                borderRadius: 3.5,
                                borderColor: 'rgba(15, 23, 42, 0.08)',
                                boxShadow: '0 12px 28px rgba(15, 23, 42, 0.04)',
                            }}
                        >
                            <CardContent sx={{ p: 1.75 }}>
                                <Stack spacing={1.5}>
                                    <Typography variant="subtitle1">{formatCountryCode(snapshot.countryCode)}</Typography>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 1,
                                            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                                        }}
                                    >
                                        <SummaryTile
                                            label="Registrations Today"
                                            value={formatNumber(snapshot.registeredUsers.today)}
                                            accent="#2563eb"
                                        />
                                        <SummaryTile
                                            label="Practice Today"
                                            value={formatNumber(snapshot.practiceAttempts.today)}
                                            accent="#0369a1"
                                        />
                                        <SummaryTile
                                            label="Mock Tests Today"
                                            value={formatNumber(snapshot.mockTestsTaken.today)}
                                            accent="#7c3aed"
                                        />
                                        <SummaryTile
                                            label="Chats Today"
                                            value={formatNumber(snapshot.chatMessages.today)}
                                            accent="#ea580c"
                                        />
                                        <SummaryTile
                                            label="Chats This Week"
                                            value={formatNumber(snapshot.chatMessages.currentWeekMondayToSunday)}
                                            accent="#f97316"
                                        />
                                        <SummaryTile
                                            label="Active Users Today"
                                            value={formatNumber(snapshot.uniqueActiveUsers.today)}
                                            accent="#dc2626"
                                        />
                                        <Box sx={{ gridColumn: '1 / -1' }}>
                                            <SummaryTile
                                                label="Active Users This Week"
                                                value={formatNumber(snapshot.uniqueActiveUsers.currentWeekMondayToSunday)}
                                                accent="#b91c1c"
                                            />
                                        </Box>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                    {rows.length === 0 ? <Alert severity="info">No country snapshot data available.</Alert> : null}
                </Stack>
            </SectionCard>
        );
    }

    return (
        <SectionCard
            title="Country Snapshots"
            subtitle="Country-level today and weekly activity, including the new chat activity windows."
        >
            <TableContainer sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Country</TableCell>
                            <TableCell align="right">Registrations Today</TableCell>
                            <TableCell align="right">Practice Today</TableCell>
                            <TableCell align="right">Mock Tests Today</TableCell>
                            <TableCell align="right">Chats Today</TableCell>
                            <TableCell align="right">Chats This Week</TableCell>
                            <TableCell align="right">Active Users Today</TableCell>
                            <TableCell align="right">Active Users This Week</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((snapshot) => (
                            <TableRow key={snapshot.countryCode} hover>
                                <TableCell>{formatCountryCode(snapshot.countryCode)}</TableCell>
                                <TableCell align="right">{formatNumber(snapshot.registeredUsers.today)}</TableCell>
                                <TableCell align="right">{formatNumber(snapshot.practiceAttempts.today)}</TableCell>
                                <TableCell align="right">{formatNumber(snapshot.mockTestsTaken.today)}</TableCell>
                                <TableCell align="right">{formatNumber(snapshot.chatMessages.today)}</TableCell>
                                <TableCell align="right">
                                    {formatNumber(snapshot.chatMessages.currentWeekMondayToSunday)}
                                </TableCell>
                                <TableCell align="right">{formatNumber(snapshot.uniqueActiveUsers.today)}</TableCell>
                                <TableCell align="right">
                                    {formatNumber(snapshot.uniqueActiveUsers.currentWeekMondayToSunday)}
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={8} align="center">
                                    No country snapshot data available.
                                </TableCell>
                            </TableRow>
                        ) : null}
                    </TableBody>
                </Table>
            </TableContainer>
        </SectionCard>
    );
}

export function BusinessOverviewPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const baseCurrency = defaultBaseCurrency;
    const activeCountryFilter = 'ALL';
    const [data, setData] = useState<BusinessOverviewData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let isActive = true;
        let isRefreshInFlight = false;

        async function loadOverview(showLoading = true) {
            if (isRefreshInFlight) return;

            isRefreshInFlight = true;
            if (showLoading) {
                setIsLoading(true);
            }
            setError('');

            try {
                const result = await getBusinessOverview(baseCurrency);
                if (!isActive) return;
                setData(result);
            } catch (loadError) {
                if (!isActive) return;
                const message = loadError instanceof Error ? loadError.message : 'Unable to load business overview';
                setError(message);
            } finally {
                isRefreshInFlight = false;
                if (isActive) {
                    if (showLoading) {
                        setIsLoading(false);
                    }
                }
            }
        }

        void loadOverview();
        const refreshIntervalId = window.setInterval(() => {
            void loadOverview(false);
        }, businessOverviewRefreshIntervalMs);

        return () => {
            isActive = false;
            window.clearInterval(refreshIntervalId);
        };
    }, [baseCurrency]);

    const activityMetrics = useMemo(
        () =>
            data
                ? [
                      { label: 'Practice Attempts', value: data.activity.practiceAttempts },
                      { label: 'Mock Tests Taken', value: data.activity.mockTestsTaken },
                      { label: 'Chat Messages', value: data.activity.chatMessages },
                      { label: 'Unique Practice Users', value: data.activity.uniquePracticeUsers },
                      { label: 'Unique Mock Test Users', value: data.activity.uniqueMockTestUsers },
                      { label: 'Unique Chat Users', value: data.activity.uniqueChatUsers },
                      { label: 'Unique Active Users', value: data.activity.uniqueActiveUsers },
                  ]
                : [],
        [data],
    );

    const filteredSalesByCountry = useMemo(
        () =>
            activeCountryFilter === 'ALL'
                ? (data?.salesToday.byCountry ?? [])
                : (data?.salesToday.byCountry ?? []).filter((row) => row.countryCode === activeCountryFilter),
        [activeCountryFilter, data],
    );

    const filteredSalesByCurrency = useMemo(() => {
        if (!data) return [];
        return activeCountryFilter === 'ALL'
            ? data.salesToday.byCurrency
            : buildFilteredSalesByCurrency(filteredSalesByCountry);
    }, [activeCountryFilter, data, filteredSalesByCountry]);

    const filteredCountrySnapshots = useMemo(
        () =>
            activeCountryFilter === 'ALL'
                ? (data?.countrySnapshots ?? [])
                : (data?.countrySnapshots ?? []).filter((row) => row.countryCode === activeCountryFilter),
        [activeCountryFilter, data],
    );

    const focusedCountrySnapshot = useMemo<BusinessOverviewCountrySnapshot | null>(
        () => filteredCountrySnapshots[0] ?? null,
        [filteredCountrySnapshots],
    );

    const headlineCards = data
        ? [
              {
                  label: 'New Registrations',
                  value: formatNumber(data.headline.registeredUsersToday),
                  caption: `Yesterday ${formatNumber(data.registrations.users.yesterday)} • Week ${formatNumber(data.registrations.users.currentWeekMondayToSunday)}`,
                  metric: data.registrations.users,
                  icon: <PersonAddAlt1RoundedIcon fontSize="small" />,
                  accent: '#2563eb',
              },
              {
                  label: 'Users Creating Orders',
                  value: formatNumber(data.ordersToday.distinctUsers),
                  caption: `${formatNumber(data.ordersToday.orderCount)} orders created today across all payment statuses`,
                  icon: <PaymentsRoundedIcon fontSize="small" />,
                  accent: '#9333ea',
              },
              {
                  label: 'Purchased Users',
                  value: formatNumber(data.headline.purchasedUsersToday),
                  caption: `${formatNumber(data.salesToday.purchaseCount)} purchases across ${formatNumber(data.salesToday.distinctCountries)} countries`,
                  icon: <PaymentsRoundedIcon fontSize="small" />,
                  accent: '#0f766e',
              },
              {
                  label: `Approximate Sales (${data.baseCurrency})`,
                  value: formatCurrency(data.headline.approximateSalesTodayInBaseCurrency, data.baseCurrency),
                  caption: `${formatNumber(data.salesToday.distinctCurrencies)} currencies converted into ${data.baseCurrency}`,
                  icon: <PaymentsRoundedIcon fontSize="small" />,
                  accent: '#15803d',
              },
              {
                  label: 'Practice Attempts',
                  value: formatNumber(data.headline.practiceAttemptsToday),
                  caption: `Yesterday ${formatNumber(data.activity.practiceAttempts.yesterday)} • Week ${formatNumber(data.activity.practiceAttempts.currentWeekMondayToSunday)}`,
                  metric: data.activity.practiceAttempts,
                  icon: <SchoolRoundedIcon fontSize="small" />,
                  accent: '#0369a1',
              },
              {
                  label: 'Mock Tests',
                  value: formatNumber(data.headline.mockTestsTakenToday),
                  caption: `Yesterday ${formatNumber(data.activity.mockTestsTaken.yesterday)} • Week ${formatNumber(data.activity.mockTestsTaken.currentWeekMondayToSunday)}`,
                  metric: data.activity.mockTestsTaken,
                  icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
                  accent: '#7c3aed',
              },
              {
                  label: 'Chat Messages',
                  value: formatNumber(data.headline.chatMessagesToday),
                  caption: `Yesterday ${formatNumber(data.activity.chatMessages.yesterday)} • Week ${formatNumber(data.activity.chatMessages.currentWeekMondayToSunday)}`,
                  metric: data.activity.chatMessages,
                  icon: <ForumRoundedIcon fontSize="small" />,
                  accent: '#ea580c',
              },
              {
                  label: 'Unique Practice Users',
                  value: formatNumber(data.activity.uniquePracticeUsers.today),
                  caption: `Yesterday ${formatNumber(data.activity.uniquePracticeUsers.yesterday)} • Week ${formatNumber(data.activity.uniquePracticeUsers.currentWeekMondayToSunday)}`,
                  metric: data.activity.uniquePracticeUsers,
                  icon: <SchoolRoundedIcon fontSize="small" />,
                  accent: '#0f766e',
              },
          ]
        : [];

    const primaryTrendCards = data
        ? [
              {
                  label: 'Registrations',
                  metric: data.registrations.users,
                  caption: 'Daily signups compared with yesterday and the current weekly pace.',
                  icon: <PersonAddAlt1RoundedIcon fontSize="small" />,
                  accent: '#2563eb',
              },
              {
                  label: 'Practice Attempts',
                  metric: data.activity.practiceAttempts,
                  caption: 'Practice volume trend across the current reporting window.',
                  icon: <SchoolRoundedIcon fontSize="small" />,
                  accent: '#0369a1',
              },
              {
                  label: 'Chat Messages',
                  metric: data.activity.chatMessages,
                  caption: 'Chat activity is now tracked alongside assessment usage in the same window.',
                  icon: <ForumRoundedIcon fontSize="small" />,
                  accent: '#ea580c',
              },
              {
                  label: 'Unique Active Users',
                  metric: data.activity.uniqueActiveUsers,
                  caption: 'Combined learner activity across practice, mock tests, and chat.',
                  icon: <GroupsRoundedIcon fontSize="small" />,
                  accent: '#dc2626',
              },
          ]
        : [];

    const engagementTrendCards = data
        ? [
              {
                  label: 'Mock Tests Taken',
                  metric: data.activity.mockTestsTaken,
                  caption: 'Mock test starts for the day versus yesterday and weekly average.',
                  icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
                  accent: '#7c3aed',
              },
              {
                  label: 'Unique Practice Users',
                  metric: data.activity.uniquePracticeUsers,
                  caption: 'Distinct learners using practice today within the current reporting window.',
                  icon: <SchoolRoundedIcon fontSize="small" />,
                  accent: '#0f766e',
              },
              {
                  label: 'Unique Mock Test Users',
                  metric: data.activity.uniqueMockTestUsers,
                  caption: 'Distinct learners starting mock tests today.',
                  icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
                  accent: '#8b5cf6',
              },
              {
                  label: 'Unique Chat Users',
                  metric: data.activity.uniqueChatUsers,
                  caption: 'Distinct learners sending chat messages during the same time window.',
                  icon: <ForumRoundedIcon fontSize="small" />,
                  accent: '#f97316',
              },
          ]
        : [];

    const focusedCountryCards = focusedCountrySnapshot
        ? [
              {
                  label: 'Country Registrations',
                  metric: focusedCountrySnapshot.registeredUsers,
                  caption: 'Registrations inside the selected country window.',
                  icon: <PersonAddAlt1RoundedIcon fontSize="small" />,
                  accent: '#2563eb',
              },
              {
                  label: 'Country Practice',
                  metric: focusedCountrySnapshot.practiceAttempts,
                  caption: 'Practice attempts generated from the selected country.',
                  icon: <SchoolRoundedIcon fontSize="small" />,
                  accent: '#0369a1',
              },
              {
                  label: 'Country Mock Tests',
                  metric: focusedCountrySnapshot.mockTestsTaken,
                  caption: 'Mock test starts for the selected country.',
                  icon: <AssignmentTurnedInRoundedIcon fontSize="small" />,
                  accent: '#7c3aed',
              },
              {
                  label: 'Country Chat Messages',
                  metric: focusedCountrySnapshot.chatMessages,
                  caption: 'Chat activity by learners inside the selected country window.',
                  icon: <ForumRoundedIcon fontSize="small" />,
                  accent: '#ea580c',
              },
              {
                  label: 'Country Active Users',
                  metric: focusedCountrySnapshot.uniqueActiveUsers,
                  caption: 'Combined active learners in the selected country.',
                  icon: <GroupsRoundedIcon fontSize="small" />,
                  accent: '#dc2626',
              },
          ]
        : [];

    return (
        <Box sx={{ pb: 3, width: '100%', minWidth: 0 }}>
            <Title title="Business Overview" />
            {isLoading ? <LinearProgress /> : null}

            <Stack spacing={3} sx={{ width: '100%', minWidth: 0 }}>
                {error ? <Alert severity="error">{error}</Alert> : null}

                {data && activeCountryFilter !== 'ALL' ? (
                    <Alert severity="info">
                        Country filtering is applied to sales and country snapshot sections for{' '}
                        {formatCountryCode(activeCountryFilter)}. Headline and hourly trend totals remain global
                        because the endpoint returns aggregate time-series metrics only.
                    </Alert>
                ) : null}

                {data ? (
                    <>
                        <Box
                            sx={{
                                ...getMobileRailSx(),
                                gap: 2,
                                gridTemplateColumns: {
                                    sm: 'repeat(2, minmax(0, 1fr))',
                                    xl: 'repeat(4, minmax(0, 1fr))',
                                },
                            }}
                        >
                            {headlineCards.map((card) => (
                                <KpiCard
                                    key={card.label}
                                    label={card.label}
                                    value={card.value}
                                    caption={card.caption}
                                    metric={card.metric}
                                    icon={card.icon}
                                    accent={card.accent}
                                />
                            ))}
                        </Box>

                        <OrdersTodayTable
                            rows={data.ordersToday.details}
                            orderCount={data.ordersToday.orderCount}
                            returnedDetailCount={data.ordersToday.returnedDetailCount}
                            isTruncated={data.ordersToday.isTruncated}
                            mobile={isMobile}
                        />

                        <SectionCard
                            title="Today vs Yesterday by Time"
                            subtitle="Compare cumulative registrations and practice attempts at matching times, including a live same-minute checkpoint."
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gap: 2,
                                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', xl: 'repeat(2, minmax(0, 1fr))' },
                                }}
                            >
                                <HourlyComparisonChart
                                    title="New Registrations"
                                    points={data.hourlyTrends.registrations}
                                    accent="#2563eb"
                                    todayDate={data.calendar.todayLocalDate}
                                    yesterdayDate={data.calendar.yesterdayLocalDate}
                                />
                                <HourlyComparisonChart
                                    title="Practice Attempts"
                                    points={data.hourlyTrends.practiceAttempts}
                                    accent="#0369a1"
                                    todayDate={data.calendar.todayLocalDate}
                                    yesterdayDate={data.calendar.yesterdayLocalDate}
                                />
                            </Box>
                        </SectionCard>

                        <SectionCard
                            title="Core Trends"
                            subtitle="The primary activity lines for the day, including the new chat activity stream."
                        >
                            <Box
                                sx={{
                                    ...getMobileRailSx(),
                                    gap: 2,
                                    gridTemplateColumns: {
                                        md: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                {primaryTrendCards.map((item) => (
                                    <MetricTrendCard
                                        key={item.label}
                                        label={item.label}
                                        metric={item.metric}
                                        caption={item.caption}
                                        icon={item.icon}
                                        accent={item.accent}
                                    />
                                ))}
                            </Box>
                        </SectionCard>

                        <SectionCard
                            title="Engagement Channels"
                            subtitle="Break down activity by channel and distinct learner participation across the same reporting window."
                        >
                            <Box
                                sx={{
                                    ...getMobileRailSx(),
                                    gap: 2,
                                    gridTemplateColumns: {
                                        md: 'repeat(2, minmax(0, 1fr))',
                                        xl: 'repeat(4, minmax(0, 1fr))',
                                    },
                                }}
                            >
                                {engagementTrendCards.map((item) => (
                                    <MetricTrendCard
                                        key={item.label}
                                        label={item.label}
                                        metric={item.metric}
                                        caption={item.caption}
                                        icon={item.icon}
                                        accent={item.accent}
                                    />
                                ))}
                            </Box>
                        </SectionCard>

                        {focusedCountrySnapshot ? (
                            <SectionCard
                                title={`Country Focus: ${formatCountryCode(focusedCountrySnapshot.countryCode)}`}
                                subtitle="Country-level activity cards now include chat traffic alongside registrations, practice, mock tests, and active learners."
                            >
                                <Box
                                    sx={{
                                        ...getMobileRailSx(),
                                        gap: 2,
                                        gridTemplateColumns: {
                                            md: 'repeat(2, minmax(0, 1fr))',
                                            xl: 'repeat(5, minmax(0, 1fr))',
                                        },
                                    }}
                                >
                                    {focusedCountryCards.map((item) => (
                                        <MetricTrendCard
                                            key={item.label}
                                            label={item.label}
                                            metric={item.metric}
                                            caption={item.caption}
                                            icon={item.icon}
                                            accent={item.accent}
                                        />
                                    ))}
                                </Box>
                            </SectionCard>
                        ) : null}

                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    xl: 'minmax(0, 0.85fr) minmax(0, 1.15fr)',
                                },
                            }}
                        >
                            <WindowMetricTable
                                title="Registrations Window"
                                subtitle="Registration counts across today, yesterday, and the current Monday-to-Sunday week."
                                metrics={[{ label: 'Registered Users', value: data.registrations.users }]}
                                mobile={isMobile}
                            />
                            <WindowMetricTable
                                title="Activity Window"
                                subtitle="All activity windows, including chat messages and unique chat users."
                                metrics={activityMetrics}
                                mobile={isMobile}
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'grid',
                                gap: 2,
                                gridTemplateColumns: {
                                    xs: '1fr',
                                    xl: 'minmax(0, 1.4fr) minmax(0, 1fr)',
                                },
                            }}
                        >
                            <SalesByCountryTable
                                rows={filteredSalesByCountry}
                                baseCurrency={data.baseCurrency}
                                mobile={isMobile}
                            />
                            <SalesByCurrencyTable
                                rows={filteredSalesByCurrency}
                                baseCurrency={data.baseCurrency}
                                mobile={isMobile}
                            />
                        </Box>

                        <CountrySnapshotsTable rows={filteredCountrySnapshots} mobile={isMobile} />
                    </>
                ) : null}
            </Stack>
        </Box>
    );
}
